import { useState } from "react";
import trainingHistoryService from "./trainingHistoryService";
import { useNavigate } from "react-router";
import { Space, Tooltip } from "antd";
import { DatabaseOutlined, FieldTimeOutlined, AimOutlined, ReadOutlined } from "@ant-design/icons";
import { useQuery } from "react-query";
import useDebounce from "../../../core/hooks/useDebounce.js";

const useTrainingHistory = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [options, setOptions] = useState({});
  const { debouncedValue } = useDebounce(searchQuery, 1000);

  const getTrainingsOptions = {
    ...options,
    search: debouncedValue,
    includeUpcoming: false,
  };
  const {
    data: trainings,
    isLoading,
    isFetching,
    error,
  } = useQuery(
    ["trainingsData", debouncedValue, options],
    () => trainingHistoryService.getTrainings(getTrainingsOptions),
    {
      initialData: {
        data: { data: [], meta: { hasPrevious: false, hasNext: false } },
      },
      keepPreviousData: true,
    }
  );
  if (trainings) {
    trainings.data.data = trainings.data.data.map((training, idx) => {
      return {
        ...training,
        courseTitle: training.course.title,
        start_date: training.start_date.split("T")[0],
        trainerName: `${training.trainer.firstName} ${training.trainer.lastName}`,
        index:
          trainings.data.meta.page === 1
            ? idx + 1
            : (trainings.data.meta.page - 1) * trainings.data.meta.take +
              (idx + 1),
        key: idx,
      };
    });
  }

  const navigate = useNavigate();
  const navigateToAttendanceReport = (training) => {
    return (e) => {
      e.preventDefault();
      navigate(`/attendenceReport/${training.id}`);
    };
  };

  const navigateToTrainingEvaluationReport = (training) => {
    return (e) => {
      e.preventDefault();
      navigate(`/TrainingReport/${training.id}`);
    };
  };

  const navigateToTechnicalObjectives = (training) => {
    return (e) => {
      e.preventDefault();
      navigate(`/technicalObjectives/${training.id}`);
    };
  };

  const navigateToBehavioralObjectives = (training) => {
    return (e) => {
      e.preventDefault();
      navigate(`/behavioralObjectives/${training.id}`);
    };
  };

  const onChange = (pagination, filters, sorter, extra) => {
    setOptions({
      ...options,
      page: pagination.current,
      take: pagination.pageSize,
    });
  };

  const handleSearchChange = (event) => {
    if (event.target.value.length >= 2) {
      setSearchQuery(event.target.value);
    } else {
      setSearchQuery("");
    }
  };

  const columns = [
    {
      title: "  ",
      dataIndex: "index",
      key: "index",
    },
    {
      title: "Course Name",
      dataIndex: "courseTitle",
      key: "courseNameRef",
    },
    {
      title: "Start date",
      dataIndex: "start_date",
      key: "start_date",
    },
    {
      title: "Trainer",
      dataIndex: "trainerName",
      key: "Trainer",
    },
    {
      title: "Reports",
      key: "reports",
      render: (record) => (
        <Space size="middle">
          <Tooltip title="Attendance Report">
            <DatabaseOutlined
              onClick={navigateToAttendanceReport(record)}
              className="icon DatabaseOutlinediconColor"
            />
          </Tooltip>
          <Tooltip title="Evaluation Reports">
            <FieldTimeOutlined
              onClick={navigateToTrainingEvaluationReport(record)}
              className="icon FieldTimeOutlinediconColor"
            />
          </Tooltip>
          <Tooltip title="Technical Objectives">
            <AimOutlined
              onClick={navigateToTechnicalObjectives(record)}
              className="icon DatabaseOutlinediconColor"
            />
          </Tooltip>
          <Tooltip title="Behavioral Objectives">
            <ReadOutlined
              onClick={navigateToBehavioralObjectives(record)}
              className="icon FieldTimeOutlinediconColor"
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return {
    trainings: trainings.data,
    isLoading,
    isFetching,
    error,
    searchQuery,
    setSearchQuery,
    options,
    setOptions,
    navigateToAttendanceReport,
    columns,
    onChange,
    handleSearchChange,
  };
};
export default useTrainingHistory;
